/**
 * MyWritingDashboard — the writer's home. Continue Writing first, then the
 * full story grid with search/sort/filter.
 */

import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, UploadCloud, Search } from 'lucide-react';
import { fadeUp, stagger, viewport } from '@/lib/motion';
import AppHeader from '@/components/home/AppHeader';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/site/primitives';
import { useMyWriting } from './context/MyWritingContext';
import { useModalState } from './hooks';
import { DASHBOARD_TABS, SORT_OPTIONS } from './constants';
import { sortStories } from './utils/storyHelpers';
import StoryCard from './StoryCard';
import ContinueWritingCard from './ContinueWritingCard';
import EmptyState from './EmptyState';
import NewStoryModal from './NewStoryModal';
import ImportDraftModal from './ImportDraftModal';

export default function MyWritingDashboard() {
  const { stories } = useMyWriting();
  const [isNewOpen, openNew, closeNew] = useModalState(false);
  const [isImportOpen, openImport, closeImport] = useModalState(false);
  const [searchParams] = useSearchParams();
  // Deep-linking support, e.g. the profile menu's "Drafts" shortcut
  // (/app/studio?tab=draft) should land straight on that tab.
  const [tab, setTab] = useState(() => searchParams.get('tab') ?? 'all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('updatedAt');

  const activeStories = useMemo(() => stories.filter((s) => s.status !== 'archived'), [stories]);
  const continueStory = useMemo(
    () => (activeStories.length ? sortStories(activeStories, 'updatedAt')[0] : null),
    [activeStories]
  );

  const tabFiltered = useMemo(() => {
    if (tab === 'all') return stories.filter((s) => s.status !== 'archived');
    if (tab === 'draft') return stories.filter((s) => s.status === 'draft');
    if (tab === 'published') return stories.filter((s) => s.status === 'published' || s.status === 'completed');
    if (tab === 'archived') return stories.filter((s) => s.status === 'archived');
    return stories;
  }, [stories, tab]);

  const visibleStories = useMemo(() => {
    let result = tabFiltered;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((s) => s.title.toLowerCase().includes(q) || s.genre?.toLowerCase().includes(q));
    }
    return sortStories(result, sortBy);
  }, [tabFiltered, search, sortBy]);

  const hasAnyStories = stories.length > 0;

  return (
    <div className="min-h-screen bg-cream" data-testid="my-writing-page">
      <AppHeader />
      <div className="u-container py-10 pb-28 md:py-14 md:pb-16">
      <motion.div variants={fadeUp} initial="hidden" animate="show" className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">My Manuscripts</h1>
          <p className="mt-2 max-w-md text-base text-subtle">Your stories, your ideas, your little corner to write.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          <button
            type="button"
            onClick={openImport}
            data-testid="my-writing-import-cta"
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-card px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:border-coral/40 hover:text-coral"
          >
            <UploadCloud className="h-3.5 w-3.5" /> Import Draft
          </button>
          <Button variant="primary" icon={false} onClick={openNew} data-testid="my-writing-new-cta">
            <Plus className="mr-1.5 h-4 w-4" /> New Manuscript
          </Button>
        </div>
      </motion.div>

      {!hasAnyStories ? (
        <EmptyState type="noStories" onCTAClick={openNew} />
      ) : (
        <>
          {continueStory && (
            <div className="mt-8">
              <ContinueWritingCard story={continueStory} />
            </div>
          )}

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="mt-10">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-display text-2xl font-semibold text-ink">My Manuscripts</h2>

              <div className="flex flex-wrap items-center gap-2.5">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-subtle" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search manuscripts…"
                    data-testid="my-writing-search"
                    className="w-48 rounded-full border-line bg-card pl-8 text-sm"
                  />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-44 rounded-full border-line bg-card text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-5">
              <TabsList className="h-auto rounded-full border border-line bg-card p-1">
                {DASHBOARD_TABS.map((t) => (
                  <TabsTrigger
                    key={t.key}
                    value={t.key}
                    data-testid={`my-writing-tab-${t.key}`}
                    className="rounded-full px-4 py-1.5 text-xs font-semibold data-[state=active]:bg-coral-fill data-[state=active]:text-[#FFF7F0]"
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {visibleStories.length === 0 ? (
              <EmptyState
                type={search.trim() ? 'search' : tab === 'all' ? 'inProgress' : tab}
                onCTAClick={tab === 'draft' || tab === 'all' ? openNew : undefined}
                size="sm"
              />
            ) : (
              <motion.div
                variants={stagger}
                initial="hidden"
                animate="show"
                className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                data-testid="my-writing-grid"
              >
                {visibleStories.map((story) => (
                  <motion.div key={story.id} variants={fadeUp}>
                    <StoryCard story={story} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </motion.div>
        </>
      )}

      <NewStoryModal isOpen={isNewOpen} onClose={closeNew} />
      <ImportDraftModal isOpen={isImportOpen} onClose={closeImport} />
      </div>
    </div>
  );
}
